import { format, isAfter, isBefore, parse, parseISO } from 'date-fns'

import { DDELicensePeriodPayload } from '@img-arena/consoles-api-dde'
import { DATE_FORMAT } from '@/constants/dateFormat'
import {
  MSFP_LICENSE_PROPERTY_OBJECTS,
  TABLE_TENNIS_LICENSE_SPORT,
  USE_COMPETITIONS
} from '@/constants/licenses'
import { SPORT } from '@/constants/sport'
import { LabelValue } from '@/types/common'
import {
  License,
  LicensePeriodWithSeasons,
  LicenseProperty,
  LicenseWithSeasons,
  ParsedLicensePeriod
} from '@/types/dde/license'
import type { Competition, Season } from '@/types/dde/season'
import { FormattedDGELicense, ParsedDGELicensePeriod } from '@/types/streams/license'
import { LicenseProperties } from '@/utils/api/licenseProperties'
import { placeholderSeasonObject } from '@/utils/placeholderSeasons'

export const isMigratedToMSFPSport = (sport: string) => {
  return sport === TABLE_TENNIS_LICENSE_SPORT || sport === SPORT.VOLLEYBALL
}

/**
 * Checks if a license property is msfp e.g. SOCCER_CORE, and should have an array of competition ids
 */
export const isMsfpLicense = (licenseProperty?: string | number) =>
  !!getLicensePropertySeasonsEndpoint(licenseProperty)

// TODO Remove once our REST.api map in Data Console is available via console-api
export const getLicensePropertySeasonsEndpoint = (
  licenseProperty?: LicenseProperty | string | number
) => MSFP_LICENSE_PROPERTY_OBJECTS.find((property) => property.value === licenseProperty)?.endpoint

export const getLicensePropertySport = (licenseProperty: LicenseProperty | string) =>
  MSFP_LICENSE_PROPERTY_OBJECTS.find((property) => property.value === licenseProperty)?.sport

/**
 * @param licenseProperty The license property as a string
 * @param license The raw license response object
 * @param competitions The raw competitions reponse
 * @returns [selectedCompetitions, partiallySelectedCompetitions] A 2D array of label value objects of competitions split between those selected across all license periods and those that aren't
 */
export const getSelectedCompetitions = (
  licenseProperty: string,
  license: License | LicenseWithSeasons | FormattedDGELicense[],
  competitions: Competition[]
): LabelValue[][] => {
  const licensedProperty = license.find((property) => property.property === licenseProperty)
  if (
    !licensedProperty ||
    !licensedProperty?.licensingPeriods?.length ||
    !('subscription' in licensedProperty.licensingPeriods[0]) ||
    !licensedProperty.licensingPeriods?.[0]?.subscription?.competitionIds?.length
  ) {
    return []
  }
  const numberOfLicensePeriods = licensedProperty.licensingPeriods.length
  const licensePeriodCompetitions = licensedProperty.licensingPeriods.map((licensePeriod) => {
    if (!('subscription' in licensePeriod)) return []
    return licensePeriod.subscription?.competitionIds || []
  })

  const flattenedLicensePeriodCompetitions = licensePeriodCompetitions.flat()

  // Deduplicate
  const uniqueCompetitionIds = Array.from(new Set(flattenedLicensePeriodCompetitions))

  // Create a map of the fetched competitions
  const competitionsMap = competitions.reduce(
    (competitionsMap: { [key: string]: Competition }, competition) => ({
      ...competitionsMap,
      [competition.id]: competition
    }),
    {}
  )

  // Convert to LabelValue[]
  const formattedCompetitionIds = uniqueCompetitionIds.map((competitionId) => {
    return { label: competitionsMap[competitionId]?.name || competitionId, value: competitionId }
  })

  // Extract competitions that are partially selected between license periods to their own array
  return formattedCompetitionIds.reduce(
    ([selected, partiallySelected]: LabelValue[][], competition) => {
      const occurrenceTimes = flattenedLicensePeriodCompetitions.reduce(
        (accumulator, compId) => (compId === competition.value ? accumulator + 1 : accumulator),
        0
      )
      if (occurrenceTimes !== numberOfLicensePeriods) {
        // Partially selected need to be added to both
        return [
          [...selected, competition],
          [...partiallySelected, competition]
        ]
      }
      return [[...selected, competition], partiallySelected]
    },
    [[], []]
  )
}

/**
 * Converts a seasons response array to an array of label - value objects
 * @param season Element of the array provided from the seasons api
 */
export const seasonToLabelValueObject = (season: Season): LabelValue => {
  return { label: `${season.competition.name}, ${season.name}`, value: season.competition.id }
}

/**
 * Filters an array of Seasons by a provided competition ID
 * @param season Element of the array provided from the seasons api
 * @param competitionId ID string of the assigned competition to a licensing period === season.competition.id
 */
export const doesSeasonMatchCompetitionId = (season: Season, competitionId: string) => {
  return season.competition.id === competitionId
}

/**
 * Filters an array of Seasons if its start date is between the start and end date of a licensing period
 * @param season Element of the array provided from the seasons api
 * @param startDate The start date to filter by e.g. 2023-01-30
 * @param endDate The end date to filter by e.g. 2023-12-31
 */
export const isSeasonWithinLicensePeriodDates = (
  season: Season,
  startDate: string | Date,
  endDate: string | Date
) => {
  const licensePeriodStartDate =
    startDate instanceof Date ? startDate : parse(startDate, DATE_FORMAT.DATE, new Date())
  const licensePeriodEndDate =
    endDate instanceof Date ? endDate : parse(endDate, DATE_FORMAT.DATE, new Date())
  const seasonStartDate = parseISO(season.startDateUTC)
  const seasonEndDate = parseISO(season.endDateUTC)
  const isSeasonAfterLicenseStartDate = isAfter(seasonStartDate, licensePeriodStartDate)
  const isSeasonBeforeLicenseEndDate = isBefore(seasonStartDate, licensePeriodEndDate)
  const isSeasonEndAfterLicenseStartDate = isAfter(seasonEndDate, licensePeriodStartDate)
  const isSeasonEndBeforeLicenseEndDate = isBefore(seasonEndDate, licensePeriodEndDate)
  return (
    (isSeasonAfterLicenseStartDate && isSeasonBeforeLicenseEndDate) ||
    (isSeasonEndAfterLicenseStartDate && isSeasonEndBeforeLicenseEndDate)
  )
}

/**
 * Creates a new array of all the seasons in a given array that relate to a license periods competition ids
 * as well as the license periods start and end date.
 * Seasons only need to fit inside the license periods start and end date partially to match
 * If a season isn't found for the comp id in the period, but a seasons containing competition name is found outside,
 * return a placeholder naming the competition. Else a competition id not found placeholder
 * @param competitionIds Array of ID strings usually from a license periods subscription.competitionIds
 * @param seasons Array of seasons provided from the seasons api
 * @param licensePeriod An element from the licensingPeriods of a license property
 */
export const licensePeriodCompetitionIdsToSeasons = (
  competitionIds: string[] = [],
  seasons: Season[],
  licensePeriod: DDELicensePeriodPayload
) => {
  // get all seasons that fall within the license period date range
  const seasonsWithinLicensePeriod = seasons.filter((season: Season) =>
    isSeasonWithinLicensePeriodDates(season, licensePeriod.startDate, licensePeriod.endDate)
  )

  // get all that don't
  const seasonsOutsideLicensePeriod = seasons.filter(
    (season: Season) =>
      !isSeasonWithinLicensePeriodDates(season, licensePeriod.startDate, licensePeriod.endDate)
  )

  // get all the seasons for each competition id
  return competitionIds.reduce((seasonList: Season[], competitionId) => {
    // find seasons for the competition id within date range and add them
    const competitionSeasonsInDateRange = seasonsWithinLicensePeriod.filter((season: Season) =>
      doesSeasonMatchCompetitionId(season, competitionId)
    )

    // both competition and season
    if (competitionSeasonsInDateRange.length) {
      return [...seasonList, ...competitionSeasonsInDateRange]
    }

    // else find a name for the competition amongst the seasons outside the date range and add a placeholder
    const competitionSeasonsOutOfDateRange = seasonsOutsideLicensePeriod.filter((season: Season) =>
      doesSeasonMatchCompetitionId(season, competitionId)
    )

    // competition but no season within dates
    if (competitionSeasonsOutOfDateRange.length) {
      return [
        ...seasonList,
        placeholderSeasonObject(competitionId, competitionSeasonsOutOfDateRange[0].competition.name)
      ]
    }

    // else return a competition not found placeholder
    // no competition and no season found
    return [...seasonList, placeholderSeasonObject(competitionId)]
  }, [])
}

/**
 * Returns an array with start and end date keys for a license period property
 * @param license A license object directly retrieved from the operators license endpoint
 * @param licenseProperty The property of which to access in the license, id or name string
 */
export const licenseToPropertyStartEndDate = (
  license: LicenseWithSeasons | FormattedDGELicense[],
  licenseProperty: string
) => {
  const property = license.filter((property) =>
    'propertyData' in property
      ? property.propertyData.id === Number(licenseProperty)
      : property.property === licenseProperty
  )
  if (!property.length) return []
  return property[0].licensingPeriods.map((licensePeriod) => {
    return {
      ...licensePeriod,
      ...('subscription' in licensePeriod
        ? {
            ...licensePeriod?.subscription,
            competitionIds: licensePeriod?.subscription?.competitionIds || []
          }
        : {}),
      startDate: parse(licensePeriod.startDate, DATE_FORMAT.DATE, new Date()),
      endDate: parse(licensePeriod.endDate, DATE_FORMAT.DATE, new Date())
    } as ParsedLicensePeriod | ParsedDGELicensePeriod
  })
}

/**
 * Returns an empty competition and seasons keys for non msfp license properties4
 * @param licensePeriod An element from the licensingPeriods of a license property
 */
export const licenseWithEmptyCompSeason = (
  licensePeriod: DDELicensePeriodPayload
): LicensePeriodWithSeasons => {
  return {
    ...licensePeriod,
    subscription: {
      competitionIds: [],
      seasons: []
    }
  }
}

export const defaultLicensePeriod = (startDate: Date, endDate: Date): ParsedLicensePeriod => ({
  endDate,
  numOfBookedEvents: 0,
  numOfConsumedEvents: 0,
  numOfEvents: 0,
  startDate,
  suspended: false,
  unlimited: true,
  subscription: {
    competitionIds: []
  }
})

export const defaultStreamsLicensePeriod = (
  startDate: Date,
  endDate: Date
): ParsedDGELicensePeriod => ({
  endDate,
  startDate,
  version: 1,
  purchasedEvents: 0,
  maxEvents: 0,
  autobookingEnabled: false
})

/**
 * Creates a save or update Streams license period payload for a license property
 * @param period License period object from date selection
 */
export const createStreamsLicensePeriodPayload = (period: ParsedDGELicensePeriod) => {
  return {
    start: `${format(period.startDate, DATE_FORMAT.DATE)}T00:00:00+0000`,
    end: `${format(period.endDate, DATE_FORMAT.DATE)}T23:59:59+0000`,
    maxEvents: period.maxEvents,
    purchasedEvents: period.purchasedEvents
  }
}

/**
 * Saves a Streams license
 */

/**
 * Gets the initial year checkbox selection row numbers from a saved license
 * divisible by the amount of checkboxes per row (4 for now) to make sure that all
 * license periods are visible on load
 * @param licensePeriods parsed license period array
 * @param yearsPerRow amount of year checkboxes displayed per row on the create/edit page
 */
export const getInitialRowNums = (
  licensePeriods: (ParsedLicensePeriod | ParsedDGELicensePeriod)[],
  yearsPerRow: number
) => {
  if (!licensePeriods?.length) return yearsPerRow

  licensePeriods.sort((a, b) => (a.startDate > b.startDate ? 1 : -1))
  const today = isBefore(licensePeriods[0].startDate, new Date())
    ? licensePeriods[0].startDate
    : new Date()
  let years = yearsPerRow
  const yearPeriods = licensePeriods.reduce((acc: number[], curr) => {
    const year = curr.startDate.getFullYear()
    if (!acc.includes(year)) {
      return [...acc, year]
    }
    return acc
  }, [])

  yearPeriods.forEach((year) => {
    const isYearAfterCurrentRange = year > today.getFullYear() + years - 1
    if (isYearAfterCurrentRange) {
      years +=
        Math.trunc(Math.ceil((year - (today.getFullYear() + years - 1)) / yearsPerRow)) *
        yearsPerRow
    }
  })
  return years
}

/**
 *
 * @param licenseSearch: string
 * @param licenses: LicenseProperties
 * Given a search string this function will check filter for matching properties.
 * The key part of this function is checking through both the properties array and the key depending on the condition.
 * If the property array has values it will filter the properties that match the search string.
 * If it has an empty property array it will check if the key matches.
 * @returns filteredLicenses: LicenseProperties
 */
export const filterLicenses = (
  licenseSearch: string,
  licenses: LicenseProperties
): LicenseProperties => {
  return licenses.filter(
    (property) =>
      property.label.toLowerCase().includes(licenseSearch) ||
      property.value.toString().includes(licenseSearch)
  )
}

/**
 * Returns whether a licensed property has COMPETITIONS_ALL set, or COMPETITIONS_PARTIAL
 * @param license A license object directly retrieved from the operators license endpoint
 * @param licensePropertyName The property of which to access in the license, id or name string
 * Licenses may have been saved prior to the new field being available, in which case a default of
 * COMPETITIONS_PARTIAL should be used.
 * @returns Boolean
 */
export const isCompetitionsAllSet = (
  license: LicenseWithSeasons | FormattedDGELicense[],
  licensePropertyName: string
): boolean => {
  const licenseProperty = license.find(
    (property) => property.property.toString().toUpperCase() === licensePropertyName.toUpperCase()
  )
  if (licenseProperty) {
    return licenseProperty.licensingPeriods.some((licensePeriod) =>
      'subscription' in licensePeriod
        ? licensePeriod.subscription?.type === USE_COMPETITIONS.ALL
        : false
    )
  }
  return false
}
